import { ExcelBuilder } from "../utils/ExcelBuilder";
import { PdfBuilder } from "../utils/PdfBuilder";

/**
 * BaseReportHandler
 * Abstract class for report handlers. 
 * Provides common logic for data preparation used by both PDF and Excel.
 */
export class BaseReportHandler {
    constructor(data) {
        this.data = data;
        this.metadata = data.metadata;
        this.sections = data.sections;
    }

    /**
     * Resolves the display name for an account row.
     * Centralizes the fallback logic for account names.
     */
    resolveAccountName(row) {
        return row.auxiliar_name || 
               row.account_name || 
               row.auxiliary_name || 
               row.description || 
               row.nombre || 
               '';
    }

    /**
     * Resolves the name for a third party in a report.
     */
    resolveThirdName(row) {
        return row.nombre || row.third_name || '';
    }

    /**
     * Calculates totals for a set of rows and keys.
     * @param {any[]} rows - The data rows.
     * @param {string[]} keys - The keys identifying columns.
     * @param {string[]} currencyKeys - The keys that should be summed.
     * @param {string} label - The label for the totals row (default "TOTALES").
     */
    calculateTotals(rows, keys, currencyKeys, label = "TOTALES") {
        const totals = {};
        currencyKeys.forEach(key => totals[key] = 0);

        rows.forEach(row => {
            currencyKeys.forEach(key => {
                const val = row[key];
                if (typeof val === 'number') {
                    totals[key] += val;
                }
            });
        });

        return keys.map((key, index) => {
            if (currencyKeys.includes(key)) {
                return totals[key];
            }
            // Put the label in the first column that isn't a currency column and isn't too narrow
            // Usually the second or third column is better if the first is a date/code
            if (index === 0) return label; 
            return "";
        });
    }

    /**
     * Methods to be implemented by child classes.
     */
    async downloadExcel() {
        const builder = new ExcelBuilder(this.metadata);
        builder.addMainHeader();

        this.sections.forEach(section => {
            builder.addSectionTitle(section.name || "Detalle");
            const rows = section.data || [];
            if (rows.length === 0) return;

            const headers = Object.keys(rows[0]);
            const keys = headers;
            const dataRows = rows.map(item => keys.map(k => item[k] ?? ""));

            // Heuristic to detect currency columns: numbers that are not "id" or "code" or "account"
            const currencyKeys = keys.filter(k => {
                const val = rows[0][k];
                const lowerK = k.toLowerCase();
                return typeof val === 'number' && 
                       !lowerK.includes('id') && 
                       !lowerK.includes('code') && 
                       !lowerK.includes('account') &&
                       !lowerK.includes('cuenta') &&
                       !lowerK.includes('number') &&
                       !lowerK.includes('numero');
            });
            const currencyIndices = keys.map((k, i) => currencyKeys.includes(k) ? i : -1).filter(i => i !== -1);

            builder.addTable(headers, dataRows, {
                onRowAdded: (excelRow) => {
                    excelRow.eachCell((cell, colNumber) => {
                        if (currencyIndices.includes(colNumber - 1) && typeof cell.value === 'number') {
                            cell.numFmt = '"$"#,##0;[Red]"-"$#,##0';
                            cell.alignment = { horizontal: "right" };
                        }
                    });
                }
            });

            const totalsRowValues = this.calculateTotals(rows, keys, currencyKeys);
            builder.addTotalsRow(totalsRowValues, currencyIndices);
            builder.addRow([]);
        });

        await builder.download();
    }

    async downloadPdf(download = true) {
        const builder = new PdfBuilder(this.metadata);
        builder.addMainHeader();

        this.sections.forEach(section => {
            builder.addSectionHeader(section.name || "Detalle");
            const rows = section.data || [];
            if (rows.length === 0) return;

            const headers = Object.keys(rows[0]);
            const keys = headers;

            const currencyKeys = keys.filter(k => {
                const val = rows[0][k];
                const lowerK = k.toLowerCase();
                const isLikelyCurrencyName = 
                    lowerK.includes('base') || 
                    lowerK.includes('iva') || 
                    lowerK.includes('neto') || 
                    lowerK.includes('tax') || 
                    lowerK.includes('total') || 
                    lowerK.includes('saldo') || 
                    lowerK.includes('debito') || 
                    lowerK.includes('credito') || 
                    lowerK.includes('value') || 
                    lowerK.includes('val') || 
                    lowerK.includes('amount') || 
                    lowerK.includes('subtotal') || 
                    lowerK.includes('balance');
                
                const isNumeric = typeof val === 'number' || (typeof val === 'string' && !isNaN(parseFloat(val)) && isFinite(Number(val)));

                return isNumeric && isLikelyCurrencyName &&
                       !lowerK.includes('id') && 
                       !lowerK.includes('code') && 
                       !lowerK.includes('account') &&
                       !lowerK.includes('cuenta') &&
                       !lowerK.includes('number') &&
                       !lowerK.includes('numero');
            });
            const currencyIndices = keys.map((k, i) => currencyKeys.includes(k) ? i : -1).filter(i => i !== -1);

            const bodyRows = rows.map(item => {
                return keys.map((k) => {
                    const val = item[k];
                    const isCurrency = currencyKeys.includes(k);
                    return {
                        text: isCurrency ? builder.formatCurrency(val) : builder.formatGeneralValue(val),
                        alignment: isCurrency ? "right" : "left",
                        noWrap: isCurrency
                    };
                });
            });

            const totalsRowValues = this.calculateTotals(rows, keys, currencyKeys);
            bodyRows.push(builder.getTotalsRow(totalsRowValues, currencyIndices));

            builder.addTable(headers, bodyRows);
        });

        return builder.download(download);
    }
}
